// Nameer marketing page (nameer/templates/marketing/home.html).
// The page is complete without this script; it adds the demo store's
// language flip (GSAP Flip), the configurator, the order demo and the
// section entrances.
import { gsap } from "gsap";
import { Flip } from "gsap/Flip";

gsap.registerPlugin(Flip);

const data = JSON.parse(document.getElementById("nameer-data").textContent);
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const FLIP_INTERVAL_MS = 6500;
const STORE_COLOURS = {
	maroon: "oklch(0.43 0.135 8)",
	palm: "oklch(0.46 0.09 152)",
	sea: "oklch(0.46 0.1 232)",
	coffee: "oklch(0.43 0.065 55)",
};
const ORDER_STATUSES = ["new", "preparing", "ready", "delivery", "completed"];
const RECEIPT_KEYS = {
	new: "order_status_new",
	preparing: "order_status_preparing",
	ready: "order_status_ready",
	delivery: "order_status_delivery",
	completed: "order_status_completed",
};

const formatAmount = (value) => value.toFixed(2);

function formatMoney(value, lang) {
	const currency = data.store[lang].currency;
	return lang === "ar" ? `${formatAmount(value)} ${currency}` : `${currency} ${formatAmount(value)}`;
}

function slugify(value) {
	// Same rule as the store wizard (dashboard/src/pages/CreateStore.vue).
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 40);
}

class DemoStore {
	constructor(root) {
		this.root = root;
		this.store = root.querySelector("[data-store]");
		this.lang = this.store.lang;
		this.method = "cod";
		this.zone = 56;
		this.isOpen = true;
		this.isFlipping = false;
		this.products = Object.fromEntries(data.store.products.map((product) => [product.id, product]));
		this.subtotal = data.store.cart.reduce((sum, line) => sum + this.products[line.product].price * line.qty, 0);
	}

	get otherLang() {
		return this.lang === "ar" ? "en" : "ar";
	}

	text(key) {
		if (key.startsWith("product:")) return this.products[key.slice(8)].name[this.lang];
		return data.store[this.lang][key];
	}

	getDeliveryArea() {
		return data.store.delivery_areas.find((area) => this.zone >= area.from && this.zone <= area.to);
	}

	setLanguage(lang) {
		if (lang === this.lang || this.isFlipping) return;
		if (reducedMotion.matches) {
			this.applyLanguage(lang);
			gsap.fromTo(this.store, { autoAlpha: 0.4 }, { autoAlpha: 1, duration: 0.15 });
			return;
		}
		this.flipTo(lang);
	}

	flipTo(lang) {
		// Every block travels to its mirrored place while the words change script.
		const words = this.store.querySelectorAll("[data-t], [data-money], [data-store-title]");
		const blocks = this.store.querySelectorAll("[data-flip-id]");
		this.isFlipping = true;
		gsap.to(words, {
			autoAlpha: 0,
			duration: 0.18,
			ease: "power1.in",
			onComplete: () => {
				const state = Flip.getState(blocks);
				this.applyLanguage(lang);
				Flip.from(state, {
					duration: 0.85,
					ease: "power3.inOut",
					stagger: 0.025,
					nested: true,
					onComplete: () => (this.isFlipping = false),
				});
				gsap.to(words, { autoAlpha: 1, duration: 0.35, delay: 0.45, stagger: 0.008, ease: "power1.out" });
			},
		});
	}

	applyLanguage(lang) {
		this.lang = lang;
		this.store.lang = lang;
		this.store.dir = lang === "ar" ? "rtl" : "ltr";
		this.store.querySelectorAll("[data-t]").forEach((el) => (el.textContent = this.text(el.dataset.t)));
		this.store.querySelectorAll("[data-t-alt]").forEach((el) => (el.alt = this.text(el.dataset.tAlt)));
		this.store
			.querySelectorAll("[data-t-label]")
			.forEach((el) => el.setAttribute("aria-label", this.text(el.dataset.tLabel)));
		this.root.querySelector(".stage-demo").textContent = this.text("demo");
		this.renderName();
		this.renderCheckout();
		document.querySelectorAll('[data-control="language"]').forEach((input) => (input.checked = input.value === lang));
	}

	renderName(customName) {
		if (customName !== undefined) this.customName = customName;
		const name = this.customName || this.text("name");
		this.store.querySelector("[data-store-title]").textContent = name;
		this.store.querySelector("[data-store-initial]").textContent = name.charAt(0).toUpperCase();
	}

	renderCheckout() {
		const area = this.getDeliveryArea();
		const fee = area ? area.price : 0;
		const total = this.subtotal + fee;
		const store = this.store;

		store.querySelectorAll("[data-money]").forEach((el) => {
			if (!el.matches("[data-delivery-fee], [data-total]")) el.textContent = formatMoney(Number(el.dataset.money), this.lang);
		});
		this.updateAmount(store.querySelector("[data-delivery-fee]"), fee);
		this.updateAmount(store.querySelector("[data-total]"), total);
		store.querySelector("[data-store-address-line]").textContent = this.text("address").replace("{zone}", this.zone);

		const codAllowed = !area || area.cod;
		store.querySelectorAll("[data-method]").forEach((row) => {
			const method = row.dataset.method;
			const isCod = method === "cod" || method === "cod_card";
			const unavailable = isCod && !codAllowed;
			row.classList.toggle("is-selected", method === this.method);
			row.classList.toggle("is-unavailable", unavailable);
			row.querySelector(".co-method-note").textContent = unavailable
				? this.text("cod_unavailable").replace("{zone}", this.zone)
				: this.text(`note_${method}`);
		});
	}

	updateAmount(el, value) {
		const changed = Number(el.dataset.money) !== value;
		el.dataset.money = value;
		el.textContent = formatMoney(value, this.lang);
		if (!changed) return;
		el.classList.add("is-changed");
		clearTimeout(el.changeTimer);
		el.changeTimer = setTimeout(() => el.classList.remove("is-changed"), 900);
		if (!reducedMotion.matches) gsap.fromTo(el, { y: -6, autoAlpha: 0.3 }, { y: 0, autoAlpha: 1, duration: 0.35, ease: "power2.out" });
	}

	setScreen(screen) {
		this.store.dataset.screen = screen;
	}

	setMethod(method) {
		this.method = method;
		this.renderCheckout();
	}

	setZone(zone) {
		this.zone = zone;
		this.renderCheckout();
	}

	setColour(colour) {
		this.root.style.setProperty("--store", STORE_COLOURS[colour]);
	}

	setOpen(isOpen) {
		this.isOpen = isOpen;
		this.store.classList.toggle("is-closed", !isOpen);
		const notice = this.store.querySelector("[data-store-closed]");
		if (reducedMotion.matches) {
			notice.hidden = isOpen;
			return;
		}
		if (isOpen) {
			gsap.to(notice, { autoAlpha: 0, y: -8, duration: 0.25, onComplete: () => (notice.hidden = true) });
		} else {
			notice.hidden = false;
			gsap.fromTo(notice, { autoAlpha: 0, y: -8 }, { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" });
		}
	}
}

function setUpStage(store) {
	const stage = store.root;
	let autoplay = !reducedMotion.matches;
	let timer = null;
	let stageVisible = false;
	let heroInView = true;

	const stop = () => {
		autoplay = false;
		clearInterval(timer);
	};
	const sync = () => {
		clearInterval(timer);
		if (autoplay && stageVisible && heroInView && !document.hidden) {
			timer = setInterval(() => store.setLanguage(store.otherLang), FLIP_INTERVAL_MS);
		}
	};

	new IntersectionObserver(([entry]) => {
		stageVisible = entry.isIntersecting;
		stage.classList.toggle("is-visible", stageVisible);
		sync();
	}).observe(stage);
	new IntersectionObserver(([entry]) => {
		heroInView = entry.intersectionRatio > 0.35;
		sync();
	}, { threshold: [0, 0.35, 1] }).observe(document.querySelector(".hero"));
	document.addEventListener("visibilitychange", sync);
	reducedMotion.addEventListener("change", () => (reducedMotion.matches ? stop() : null));

	stage.querySelector("[data-stage-flip]").addEventListener("click", () => {
		stop();
		store.setLanguage(store.otherLang);
	});
	return stop;
}

function setUpControls(store, stopAutoplay) {
	document.addEventListener("change", (event) => {
		const input = event.target;
		switch (input.dataset.control) {
			case "language":
				stopAutoplay();
				store.setLanguage(input.value);
				break;
			case "method":
				store.setMethod(input.value);
				break;
			case "colour":
				store.setColour(input.value);
				break;
			case "open":
				store.setOpen(input.checked);
				break;
		}
	});

	const zoneInput = document.querySelector('[data-control="zone"]');
	const applyZone = (value) => {
		const zone = Math.min(98, Math.max(1, Math.round(Number(value)) || 1));
		zoneInput.value = zone;
		store.setZone(zone);
	};
	zoneInput.addEventListener("input", () => {
		if (zoneInput.value !== "") applyZone(zoneInput.value);
	});
	zoneInput.addEventListener("blur", () => applyZone(zoneInput.value));
	document.querySelectorAll("[data-zone-step]").forEach((button) =>
		button.addEventListener("click", () => applyZone(Number(zoneInput.value) + Number(button.dataset.zoneStep)))
	);

	const nameInput = document.querySelector("[data-store-name]");
	const placeholder = nameInput.placeholder;
	nameInput.addEventListener("input", () => {
		const slug = slugify(nameInput.value) || placeholder;
		const host = `${slug}.${data.domain}`;
		document.querySelector("[data-store-address]").textContent = host;
		document.querySelectorAll("[data-store-slug]").forEach((el) => (el.textContent = slug));
		document.querySelectorAll("[data-store-host]").forEach((el) => (el.textContent = host));
		store.renderName(nameInput.value.trim());
	});
	nameInput.closest(".address-field").addEventListener("click", () => nameInput.focus());
}

function setUpBlocks(store) {
	// The block nearest the middle of the screen chooses the store's screen.
	const blocks = document.querySelectorAll("[data-block]");
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				blocks.forEach((block) => block.classList.toggle("is-active", block === entry.target));
				store.setScreen(entry.target.dataset.block);
			});
		},
		{ rootMargin: "-45% 0px -45% 0px" }
	);
	blocks.forEach((block) => observer.observe(block));
}

function setUpOrderDemo() {
	const section = document.querySelector("[data-show]");
	const nextButton = section.querySelector("[data-order-next]");
	const payButton = section.querySelector("[data-order-pay]");
	const badge = section.querySelector("[data-order-paid]");
	const steps = section.querySelectorAll("[data-order-steps] li");
	const dots = section.querySelectorAll("[data-receipt-progress] li");
	const receiptStatus = section.querySelector("[data-receipt-status]");
	const receiptPay = section.querySelector("[data-receipt-pay]");
	const customerCopy = data.store[data.otherLang];
	const total = formatAmount(Number(receiptPay.textContent.match(/[\d.]+/)[0]));
	let index = 0;
	let paid = false;

	const render = () => {
		steps.forEach((step, i) => {
			step.classList.toggle("is-done", i <= index);
			step.toggleAttribute("aria-current", i === index);
			if (i === index) step.setAttribute("aria-current", "step");
		});
		dots.forEach((dot, i) => dot.classList.toggle("is-done", i <= index));
		receiptStatus.textContent = customerCopy[RECEIPT_KEYS[ORDER_STATUSES[index]]];
		const next = ORDER_STATUSES[index + 1];
		nextButton.textContent = next ? data.show.next.replace("{status}", data.show.statuses[next]) : data.show.restart;
		badge.textContent = paid ? data.show.paid : data.show.unpaid;
		badge.classList.toggle("is-paid", paid);
		payButton.hidden = paid;
		const payLine = customerCopy.order_pay.replace("{total}", total);
		receiptPay.textContent = paid ? `${payLine} · ${customerCopy.order_paid}` : payLine;
		if (!reducedMotion.matches) gsap.fromTo(receiptStatus, { y: 10, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.45, ease: "power2.out" });
	};

	nextButton.addEventListener("click", () => {
		index = index < ORDER_STATUSES.length - 1 ? index + 1 : 0;
		if (index === 0) paid = false;
		render();
	});
	payButton.addEventListener("click", () => {
		paid = true;
		render();
		nextButton.focus();
	});
}

function setUpHowItWorks() {
	const frame = document.querySelector("[data-how-state]");
	const steps = document.querySelectorAll("[data-how-step]");
	frame.querySelectorAll(".how-check").forEach((check, i) => check.style.setProperty("--i", i));
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				steps.forEach((step) => step.classList.toggle("is-active", step === entry.target));
				frame.dataset.howState = entry.target.dataset.howStep;
			});
		},
		{ rootMargin: "-40% 0px -40% 0px" }
	);
	steps.forEach((step) => observer.observe(step));
}

function setUpReveals() {
	document.querySelectorAll(".hero-card > *").forEach((el, i) => el.style.setProperty("--i", i));
	const groups = document.querySelectorAll("[data-reveal], .how-step");
	groups.forEach((group) => [...group.children].forEach((child, i) => child.style.setProperty("--i", Math.min(i, 4))));
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				entry.target.classList.add("is-in");
				observer.unobserve(entry.target);
			});
		},
		{ rootMargin: "0px 0px -12% 0px" }
	);
	groups.forEach((group) => observer.observe(group));
}

function setUpProvisions() {
	// The footer's one moment: what Nameer brings a store is brought in, line
	// by line from the reading end, as the footer comes into view. The colours are CSS transitions; without
	// script or with reduced motion the paragraph is simply there.
	const provisions = document.querySelector("[data-provisions]");
	if (reducedMotion.matches) return;
	provisions.querySelectorAll("li").forEach((item, i) => item.style.setProperty("--i", i));
	provisions.classList.add("is-waiting");
	const observer = new IntersectionObserver(
		([entry]) => {
			if (!entry.isIntersecting) return;
			observer.disconnect();
			provisions.classList.remove("is-waiting");
		},
		{ threshold: 0.6 }
	);
	observer.observe(provisions);
}

function setUpLanguageSwitch() {
	// Remember the choice for the dashboard too: it reads the same cookie when a merchant signs in.
	document.querySelectorAll("[data-language-switch]").forEach((link) =>
		link.addEventListener("click", () => {
			document.cookie = `preferred_language=${link.dataset.languageSwitch}; path=/; max-age=31536000; samesite=lax`;
		})
	);
}

function setUpBackToTop() {
	// Shown once the hero is out of sight. Focus goes back to the top with the
	// view, so a keyboard user does not continue from the footer.
	const link = document.querySelector("[data-to-top]");
	const update = () => link.classList.toggle("is-visible", window.scrollY > window.innerHeight);
	window.addEventListener("scroll", update, { passive: true });
	update();
	link.addEventListener("click", (event) => {
		event.preventDefault();
		window.scrollTo({ top: 0, behavior: reducedMotion.matches ? "auto" : "smooth" });
		document.querySelector(".wordmark").focus({ preventScroll: true });
	});
}

const store = new DemoStore(document.querySelector("[data-stage]"));
const stopAutoplay = setUpStage(store);
setUpControls(store, stopAutoplay);
setUpBlocks(store);
setUpOrderDemo();
setUpHowItWorks();
setUpReveals();
setUpProvisions();
setUpLanguageSwitch();
setUpBackToTop();
